import { useEffect, useState } from 'react';

import {
  NotificationPayload,
  useNotificationContext,
  useTakeUndoableMutation,
  useTranslate,
} from 'react-admin';
import { Button } from 'shared/mui/Buttons';

import { NotificationContent } from './NotificationContent';
import { NotificationStyled } from './NotificationStyled.styled';

export const Notification = () => {
  const { notifications, takeNotification } = useNotificationContext();
  const takeMutation = useTakeUndoableMutation();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationPayload>();

  useEffect(() => {
    if (notifications.length && !currentNotification) {
      const notification = takeNotification();
      if (notification) {
        setCurrentNotification(notification);
        setOpen(true);
      }
    }
  }, [notifications, currentNotification, takeNotification]);

  useEffect(() => {
    if (!currentNotification?.notificationOptions?.undoable) {
      return;
    }

    const beforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeunload);

    return () => window.removeEventListener('beforeunload', beforeunload);
  }, [currentNotification]);

  if (!currentNotification) {
    return null;
  }

  const { message, type, notificationOptions } = currentNotification;
  const { autoHideDuration = 4000, messageArgs, multiLine, undoable } = notificationOptions ?? {};

  const closeNotification = () => setOpen(false);

  const handleExited = () => {
    if (undoable) {
      takeMutation()?.({ isUndo: false });
    }
    setCurrentNotification(undefined);
  };

  const handleUndo = () => {
    takeMutation()?.({ isUndo: true });
    setOpen(false);
  };

  return (
    <NotificationStyled
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={autoHideDuration}
      disableWindowBlurListener={undoable}
      onClose={closeNotification}
      open={open}
    >
      <NotificationContent
        action={
          undoable && (
            <Button label={translate('ra.action.undo')} onClick={handleUndo} size="small" />
          )
        }
        message={typeof message === 'string' ? translate(message, messageArgs) : message}
        onClose={closeNotification}
        sx={multiLine ? { whiteSpace: 'pre-wrap' } : undefined}
        type={type}
      />
    </NotificationStyled>
  );
};
